import * as THREE from 'three';
import { HUD, ENV, GAU8 } from '../core/SimConfig.js';
import { WeaponMode } from '../weapons/WeaponSystem.js';
import { smoothstep, RAD2DEG } from '../utils.js';

const _euler = new THREE.Euler();
const _bodyVel = new THREE.Vector3();
const _invQuat = new THREE.Quaternion();
const _gunPos = new THREE.Vector3();
const _gunDir = new THREE.Vector3();
const _tmpQuat = new THREE.Quaternion();
const _dir = new THREE.Vector3();
const _pipperPos = { x: 0, y: 0 }; // canvas px of the aiming symbol just drawn (VV mask centre)
// Funnel-sight scratch
const _qOld = new THREE.Quaternion();
const _dq = new THREE.Quaternion();
const _gunLocal = new THREE.Vector3();
const _gunDirBody = new THREE.Vector3();
const _gunPosOld = new THREE.Vector3();
const _gunDirOld = new THREE.Vector3();

// Bullet drag coefficient (matches WeaponSystem): 0.5·ρ·Cd·A/m
const BULLET_DRAG_K =
  0.5 * ENV.airDensity * GAU8.bulletDragCd * GAU8.bulletArea / GAU8.bulletMass;

// Unit conversions
const MS_TO_KNOTS = 1.94384;
const M_TO_FEET = 3.28084;


export default class CockpitHUD {
  /** Load the HUD font so canvas can use it. Call once before constructing. */
  static async loadFont() {
    const face = new FontFace('HornetDisplay', 'url(assets/fonts/HornetDisplay-Regular.woff2)');
    await face.load();
    document.fonts.add(face);
  }

  constructor({ aircraft, model, cameraManager, weaponSystem = null, terrainCollider = null, windManager = null, maverickSeeker = null }) {
    this.aircraft = aircraft;
    this.cameraManager = cameraManager;
    this.weaponSystem = weaponSystem;
    this.terrainCollider = terrainCollider;
    this.windManager = windManager;
    this.maverickSeeker = maverickSeeker;  // drives the Maverick aim reticle

    // ── Find HUD mesh ────────────────────────────────────────────────────
    this.hudMesh = model.getObjectByName('_Cockpit_HUD');
    if (!this.hudMesh) {
      console.warn('CockpitHUD: "_Cockpit_HUD" mesh not found in model');
      return;
    }

    // ── Off-screen canvas ────────────────────────────────────────────────
    const res = HUD.canvasResolution;
    this.canvas = document.createElement('canvas');
    this.canvas.width = res;
    this.canvas.height = res;
    this.ctx = this.canvas.getContext('2d');

    // ── Texture + material ───────────────────────────────────────────────
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.magFilter = THREE.LinearFilter;

    // Store original material for disposal
    this._originalMaterial = this.hudMesh.material;

    this.hudMesh.material = new THREE.MeshBasicMaterial({
      map: this.texture,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
    });
    this.hudMesh.renderOrder = 999;

    // ── Apply calibration offsets ─────────────────────────────────────────
    // Store original mesh transform so offsets don't accumulate across restarts
    this._origPosition = this.hudMesh.position.clone();
    this._origRotation = this.hudMesh.rotation.clone();

    const { offset, rotation, scale } = HUD.calibration;
    this.hudMesh.position.x += offset.x;
    this.hudMesh.position.y += offset.y;
    this.hudMesh.position.z += offset.z;
    this.hudMesh.rotation.x += rotation.x;
    this.hudMesh.rotation.y += rotation.y;
    this.hudMesh.rotation.z += rotation.z;
    // Scale via texture UV repeat/offset (centering the scaled texture)
    this.texture.repeat.set(1 / scale.x, 1 / scale.y);
    // Store base UV offset for calibration (hologram effect adds to this)
    this._baseUVOffset = new THREE.Vector2(
      (1 - 1 / scale.x) / 2,
      (1 - 1 / scale.y) / 2,
    );
    this.texture.offset.copy(this._baseUVOffset);

    // ── HUD power state ─────────────────────────────────────────────────
    this.powered = aircraft.engineState.isRunning;

    // ── Drawing constants ────────────────────────────────────────────────
    this.cx = res / 2;        // canvas center X
    this.cy = res / 2;        // canvas center Y
    this.color = HUD.color;
    this.dimColor = HUD.dimColor;
    this.font = HUD.font;
    this.fontSize = HUD.fontSize;
    this.pxPerDeg = HUD.pxPerDeg; // pixels per degree for pitch ladder / velocity vector

    // ── AIM-9 seeker reticle state ──────────────────────────────────────
    // Animated screen position (smooth-tracks spin pattern or locked target)
    // and a free-running spin phase so the orbit pattern is frame-rate
    // independent. _aim9Pos is null until the first AIM-9-mode frame, so
    // re-entering the mode snaps to the desired position instead of lerping
    // in from a stale pixel.
    this._aim9Pos = null;
    this._aim9Spin = 0;
    this._aim9LastTarget = null;
    this._aim9Transition = 0;

    // ── Hologram parallax ───────────────────────────────────────────────
    // 0..1 on the head-offset UV shift: 1 from the pilot's eye point, eased to
    // 0 on an action mount, whose lens doesn't move with his head.
    this._parallax = 1;

    // ── Velocity-vector blanking windows ────────────────────────────────
    // Screen squares (canvas px) around the aiming symbols drawn this frame;
    // the VV is clipped out of each. Pooled so a frame allocates nothing.
    this._vvMasks = [];
    this._vvMaskPool = [];
  }

  // ── Public API ─────────────────────────────────────────────────────────

  update(dt = 0) {
    if (!this.hudMesh) return;

    // Only render while the view is inside the cockpit (the cockpit mode, or an
    // action mount in the canopy — either way the HUD glass is drawn).
    const isCockpit = !!this.cameraManager?.cockpitView;
    this.hudMesh.visible = isCockpit;
    if (!isCockpit) {
      // Reset AIM-9 reticle so re-entry snaps cleanly instead of lerping in
      this._aim9Pos = null;
      // Track the parallax target without easing while nothing is drawn, so
      // the HUD appears already settled for whichever view brought it up.
      this._parallax = this.cameraManager.pilotEyeView ? 1 : 0;
      return;
    }

    const a = this.aircraft;
    const ctx = this.ctx;
    const res = this.canvas.width;

    // ── HUD power state — follows engine startup/shutdown ────────────
    const eng = a.engineState;
    if (eng.isStarting)       this.powered = eng.transitionProgress >= 0.5;
    else if (eng.isStopping)  this.powered = eng.transitionProgress < 0.5;
    else                      this.powered = eng.isRunning;

    if (!this.powered) {
      ctx.clearRect(0, 0, res, res);
      this.texture.needsUpdate = true;
      return;
    }

    // ── Hologram parallax — shift UV by pilot head offset ────────────────
    // The shift fakes a collimated image for an eye that moves with the head.
    // An action mount is rigid, so there is no eye motion for it to answer and
    // the symbology would just appear to float on the glass — fade it out.
    const cockpit = this.cameraManager.cockpitCamera;
    const headOff = cockpit.localOffset;
    const parallaxTo = this.cameraManager.pilotEyeView ? 1 : 0;
    this._parallax += (parallaxTo - this._parallax) * (1 - Math.exp(-HUD.hologramBlendRate * dt));
    const hScale = HUD.hologramScale * this._parallax;
    this.texture.offset.set(
      this._baseUVOffset.x - headOff.x * hScale,
      this._baseUVOffset.y - headOff.y * hScale,
    );

    // ── Extract flight data ──────────────────────────────────────────────
    _euler.setFromQuaternion(a.quaternion, 'YXZ');
    const pitch = _euler.x;  // radians
    const roll = _euler.z;   // radians (negative = roll right)

    const airspeedKts = a.airspeed * MS_TO_KNOTS;
    const altitudeFt = a.altitude * M_TO_FEET;
    const gLoad = a.gForceLocal.y;
    const horizSpeed = Math.sqrt(a.velocity.x ** 2 + a.velocity.z ** 2);
    const fpaDeg = Math.atan2(a.velocity.y, horizSpeed) * RAD2DEG;

    // Heading from forward vector
    const fwd = a.forwardVector;
    const headingDeg = ((Math.atan2(fwd.x, -fwd.z) * RAD2DEG) + 360) % 360;

    // Velocity vector in body frame — cage to boresight below takeoff speed so
    // gear-bounce jitter (small Vy over small Vz) doesn't swing the VV wildly.
    _invQuat.copy(a.quaternion).invert();
    _bodyVel.copy(a.velocity).applyQuaternion(_invQuat);
    const fwdSpeed = -_bodyVel.z;
    const blend = smoothstep((fwdSpeed - HUD.vvCageMin) / (HUD.vvCageMax - HUD.vvCageMin));
    let vvX = 0, vvY = 0;
    if (blend > 0) {
      vvX = Math.atan2(_bodyVel.x, fwdSpeed) * RAD2DEG * blend;
      vvY = Math.atan2(_bodyVel.y, fwdSpeed) * RAD2DEG * blend;
    }

    // ── Clear ────────────────────────────────────────────────────────────
    ctx.clearRect(0, 0, res, res);

    // Flip vertically to match mesh UV orientation
    ctx.save();
    ctx.translate(0, res);
    ctx.scale(1, -1);

    // ── Draw HUD elements ────────────────────────────────────────────────
    // Weapon symbology goes down before the velocity vector: the aiming
    // symbols outrank it, and each one that's up blanks a square around
    // itself that the VV is clipped out of (see _drawVelocityVector).
    this._drawPitchLadder(ctx, pitch, roll);
    const masks = this._vvMasks;
    masks.length = 0;
    const weaponMode = this.weaponSystem?.mode;
    if (weaponMode === WeaponMode.GAU8 || weaponMode === WeaponMode.AIM9) {
      this._drawGunBoreLine(ctx);
    }
    if (weaponMode === WeaponMode.GAU8) {
      const impact = this._computeGunImpact();
      if (impact) this._pushMask(this._drawGunPipper(ctx, roll, impact), HUD.gun.maskHalfPx);
      else this._drawGunFunnel(ctx);
    }
    if (weaponMode === WeaponMode.AIM9) {
      this._drawAIM9Reticle(ctx, dt);
    } else {
      this._aim9Pos = null;
    }
    if (weaponMode === WeaponMode.AGM65) {
      this._pushMask(this._drawMaverickReticle(ctx, roll), HUD.maverick.maskHalfPx);
    }
    this._drawVelocityVector(ctx, vvX, vvY, roll, masks);
    this._drawAirspeed(ctx, airspeedKts);
    this._drawAltitude(ctx, altitudeFt);
    this._drawGMeter(ctx, gLoad);
    this._drawFPA(ctx, fpaDeg);
    this._drawHeadingTape(ctx, headingDeg);
    this._drawLowerLeft(ctx);
    this._drawLowerRight(ctx, altitudeFt);

    ctx.restore();
    this.texture.needsUpdate = true;
  }

  dispose() {
    if (this.hudMesh) {
      this.hudMesh.material.dispose();
      this.texture.dispose();
      if (this._originalMaterial) {
        this.hudMesh.material = this._originalMaterial;
      }
      // Restore original transform so offsets don't accumulate on restart
      this.hudMesh.position.copy(this._origPosition);
      this.hudMesh.rotation.copy(this._origRotation);
    }
  }

  // ── Private drawing methods ────────────────────────────────────────────

  _setFont(ctx, size, bold = false) {
    ctx.font = `${bold ? 'bold ' : ''}${size}px ${this.font}`;
  }

  // ── Pitch Ladder ───────────────────────────────────────────────────────

  _drawPitchLadder(ctx, pitch, roll) {
    const { cx, cy, pxPerDeg, color } = this;
    const pitchDeg = pitch * RAD2DEG;

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(roll); // rotate with aircraft roll

    // Clip to keep ladder contained — nearly full canvas height
    const clipH = this.canvas.height * 0.3;
    const clipW = this.canvas.width * 0.3;
    ctx.beginPath();
    ctx.rect(-clipW, -clipH, clipW * 2, clipH * 2);
    ctx.clip();

    // Translate for pitch offset (positive pitch = lines move down on screen)
    const pitchOffset = pitchDeg * pxPerDeg;

    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 2;
    this._setFont(ctx, this.fontSize);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Draw lines from -90 to +90 in 5-degree increments
    // Each line: two outer segments with a center gap
    // Positive (above horizon): solid segments, endcaps point down (toward horizon)
    // Negative (below horizon): three dashes per segment, endcaps point up (toward horizon)
    const halfLen = 120;   // outer edge distance from center
    const gapHalf = 40;    // inner gap half-width
    const capLen = 8;     // vertical endcap length
    const dashW = 20;      // dash width for negative lines
    const dashGap = 10;    // gap between dashes

    for (let deg = -90; deg <= 90; deg += 5) {
      if (deg === 0) continue; // horizon drawn separately
      const y = -deg * pxPerDeg + pitchOffset;

      // Endcap direction: positive degrees → caps point down (+y), negative → up (-y)
      const capDir = deg > 0 ? 1 : -1;

      if (deg > 0) {
        // Solid segments
        ctx.beginPath();
        // Left segment
        ctx.moveTo(-halfLen, y);
        ctx.lineTo(-gapHalf, y);
        // Right segment
        ctx.moveTo(gapHalf, y);
        ctx.lineTo(halfLen, y);
        ctx.stroke();
      } else {
        // Three dashes per segment
        ctx.beginPath();
        const segLen = halfLen - gapHalf;
        for (let side = -1; side <= 1; side += 2) {
          const start = side === -1 ? -halfLen : gapHalf;
          // Evenly space 3 dashes across the segment
          const totalDash = dashW * 3 + dashGap * 2;
          const padStart = (segLen - totalDash) / 2;
          for (let d = 0; d < 3; d++) {
            const dx = padStart + d * (dashW + dashGap);
            const x0 = side === -1 ? start + dx : start + dx;
            ctx.moveTo(x0, y);
            ctx.lineTo(x0 + dashW, y);
          }
        }
        ctx.stroke();
      }

      // Vertical endcaps at outer edges
      ctx.beginPath();
      ctx.moveTo(-halfLen, y);
      ctx.lineTo(-halfLen, y + capLen * capDir);
      ctx.moveTo(halfLen, y);
      ctx.lineTo(halfLen, y + capLen * capDir);
      ctx.stroke();

      // Degree labels outside endcaps
      const yOff = deg < 0 ? -5 : 5;
      const label = Math.abs(deg).toString().padStart(2, "0");
      ctx.fillText(label, -halfLen - 15, y + yOff);
      ctx.fillText(label, halfLen + 15, y + yOff);
    }

    // ── Horizon line (0°) ─────────────────────────────────────────────
    const hy = pitchOffset;
    ctx.lineWidth = 2;
    ctx.beginPath();
    // Left segment
    ctx.moveTo(-halfLen, hy);
    ctx.lineTo(-50, hy);
    // Right segment
    ctx.moveTo(50, hy);
    ctx.lineTo(halfLen, hy);
    ctx.stroke();

    ctx.restore();
  }

  // ── Velocity Vector (Flight Path Marker) ───────────────────────────────

  /** Record a VV blanking square of half-size `half` centred on `pos` (if drawn). */
  _pushMask(pos, half) {
    if (!pos || !(half > 0)) return;
    const i = this._vvMasks.length;
    const m = this._vvMaskPool[i] || (this._vvMaskPool[i] = { x: 0, y: 0, half: 0 });
    m.x = pos.x; m.y = pos.y; m.half = half;
    this._vvMasks.push(m);
  }

  _drawVelocityVector(ctx, vvXDeg, vvYDeg, roll, masks = null) {
    const { cx, cy, pxPerDeg, color } = this;

    // Position relative to center, rotated by aircraft roll
    const rawX = vvXDeg * pxPerDeg;
    const rawY = -vvYDeg * pxPerDeg;

    // Rotate VV position by roll to keep it in the rolled reference
    const cosR = Math.cos(roll);
    const sinR = Math.sin(roll);
    const x = cx + rawX * cosR + rawY * sinR;
    const y = cy - rawX * sinR + rawY * cosR;

    const r = 10; // circle radius
    const tailLen = 16;

    // Blanking windows: clip to the whole canvas minus each mask square
    // (even-odd), so the VV is cut cleanly where it passes under a pipper
    // instead of overprinting the aiming dot.
    const masked = masks && masks.length > 0;
    if (masked) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, this.canvas.width, this.canvas.height);
      for (const m of masks) ctx.rect(m.x - m.half, m.y - m.half, m.half * 2, m.half * 2);
      ctx.clip('evenodd');
    }

    ctx.strokeStyle = color;
    ctx.lineWidth = 2.5;

    // Circle
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.stroke();

    // Left tail
    ctx.beginPath();
    ctx.moveTo(x - r, y);
    ctx.lineTo(x - r - tailLen, y);
    ctx.stroke();

    // Right tail
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + r + tailLen, y);
    ctx.stroke();

    // Top tail
    ctx.beginPath();
    ctx.moveTo(x, y - r);
    ctx.lineTo(x, y - r - tailLen * 0.65);
    ctx.stroke();

    if (masked) ctx.restore();
  }

  // ── Gun Bore Line (boresight cross) ────────────────────────────────────

  _drawGunBoreLine(ctx) {
    const gun = this.weaponSystem?.gunNode;
    if (!gun) return;

    // Gun forward direction (local +Y) in body frame — constant per session
    // since the gun is fixed to the fuselage, so the cross stays pinned.
    _gunDir.set(0, 1, 0).applyQuaternion(gun.getWorldQuaternion(_tmpQuat));
    _invQuat.copy(this.aircraft.quaternion).invert();
    _gunDir.applyQuaternion(_invQuat);

    const fwd = -_gunDir.z;
    if (fwd <= 0) return;
    const angXDeg = Math.atan2(_gunDir.x, fwd) * RAD2DEG;
    const angYDeg = Math.atan2(_gunDir.y, fwd) * RAD2DEG;

    const x = this.cx + angXDeg * this.pxPerDeg;
    const y = this.cy - angYDeg * this.pxPerDeg;

    const arm = 5;
    ctx.strokeStyle = this.color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x - arm, y);
    ctx.lineTo(x + arm, y);
    ctx.moveTo(x, y - arm);
    ctx.lineTo(x, y + arm);
    ctx.stroke();
  }

  // ── Gun Pipper (CCIP) ──────────────────────────────────────────────────

  /**
   * Simulate a single round from the gun barrel forward, accounting for
   * gravity, aerodynamic drag and wind, and raycasting each segment against
   * terrain. Returns the impact point (world-space) if terrain is hit within
   * HUD.gun.maxRange from the muzzle, otherwise null.
   */
  _computeGunImpact() {
    const gun = this.weaponSystem?.gunNode;
    const terrain = this.terrainCollider;
    if (!gun || !terrain) return null;

    const cfg = HUD.gun;
    const a = this.aircraft;

    // Muzzle state
    gun.getWorldPosition(_gunPos);
    _gunDir.set(0, 1, 0).applyQuaternion(gun.getWorldQuaternion(_tmpQuat));

    const mx = _gunPos.x, my = _gunPos.y, mz = _gunPos.z;
    const mv = GAU8.muzzleVelocity;
    let px = mx, py = my, pz = mz;
    let vx = a.velocity.x + _gunDir.x * mv;
    let vy = a.velocity.y + _gunDir.y * mv;
    let vz = a.velocity.z + _gunDir.z * mv;

    const wind = this.windManager?.wind;
    const wx = wind ? wind.x : 0;
    const wy = wind ? wind.y : 0;
    const wz = wind ? wind.z : 0;

    const dt = cfg.simStep;
    const g = ENV.gravity;
    const maxRangeSq = cfg.maxRange * cfg.maxRange;

    for (let i = 0; i < cfg.maxSimSteps; i++) {
      // Wind-relative velocity for drag
      const rvx = vx - wx, rvy = vy - wy, rvz = vz - wz;
      const rspeed = Math.sqrt(rvx * rvx + rvy * rvy + rvz * rvz);
      const drag = BULLET_DRAG_K * rspeed;

      // Semi-implicit Euler (matches WeaponSystem)
      vx += (-drag * rvx) * dt;
      vy += (-g - drag * rvy) * dt;
      vz += (-drag * rvz) * dt;

      const nx = px + vx * dt;
      const ny = py + vy * dt;
      const nz = pz + vz * dt;

      // Terrain raycast on this segment
      const sx = nx - px, sy = ny - py, sz = nz - pz;
      const segLen = Math.sqrt(sx * sx + sy * sy + sz * sz);
      if (segLen > 0.001) {
        const hit = terrain.raycast(px, py, pz, sx, sy, sz, segLen, segLen);
        if (hit) {
          const hx = hit.x - mx, hy = hit.y - my, hz = hit.z - mz;
          if ((hx * hx + hy * hy + hz * hz) <= maxRangeSq) return hit;
          return null;
        }
      }

      px = nx; py = ny; pz = nz;

      // Past max range from muzzle — give up
      const dx = px - mx, dy = py - my, dz = pz - mz;
      if ((dx * dx + dy * dy + dz * dz) > maxRangeSq) return null;
    }
    return null;
  }

  // Returns the pipper's canvas position (shared scratch object) when drawn.
  _drawGunPipper(ctx, roll, impact) {
    const cfg = HUD.gun;
    const a = this.aircraft;

    // Direction from aircraft (body-fixed HUD reference) to impact, in body frame
    _dir.set(impact.x - a.position.x, impact.y - a.position.y, impact.z - a.position.z);
    _invQuat.copy(a.quaternion).invert();
    _dir.applyQuaternion(_invQuat);

    // Angular offsets from boresight (body -Z forward), with calibration nudge
    const fwd = -_dir.z;
    if (fwd <= 0) return; // impact behind the aircraft — don't draw
    const angXDeg = Math.atan2(_dir.x, fwd) * RAD2DEG + cfg.yawOffsetDeg;
    const angYDeg = Math.atan2(_dir.y, fwd) * RAD2DEG + cfg.pitchOffsetDeg;

    // Convert to canvas pixels, rotated by roll (same convention as VV)
    const rawX = angXDeg * this.pxPerDeg;
    const rawY = -angYDeg * this.pxPerDeg;
    const cosR = Math.cos(roll);
    const sinR = Math.sin(roll);
    const x = this.cx + rawX * cosR + rawY * sinR;
    const y = this.cy - rawX * sinR + rawY * cosR;

    const r = cfg.radiusPx;
    const n = cfg.dashCount;
    const fill = cfg.dashFill;
    const segAng = (Math.PI * 2) / n;
    const dashAng = segAng * fill;
    const startOffset = (segAng - dashAng) * 0.5;

    ctx.strokeStyle = this.color;
    ctx.fillStyle = this.color;
    ctx.lineWidth = cfg.lineWidth;

    ctx.beginPath();
    for (let i = 0; i < n; i++) {
      const a0 = i * segAng + startOffset;
      ctx.moveTo(x + r * Math.cos(a0), y + r * Math.sin(a0));
      ctx.arc(x, y, r, a0, a0 + dashAng);
    }
    ctx.stroke();

    // Center dot
    ctx.beginPath();
    ctx.arc(x, y, cfg.dotRadiusPx, 0, Math.PI * 2);
    ctx.fill();

    _pipperPos.x = x; _pipperPos.y = y;
    return _pipperPos;
  }

  // ── Air-to-air funnel sight ───────────────────────────────────────────
  //
  // Each in-flight bullet was fired from a slightly older gun pose (the
  // aircraft has been rotating and translating since). Sampling N+1 TOFs
  // and reconstructing those past poses traces the live bullet stream
  // through the rotating frame — that's what makes the funnel lean during
  // a pull. Body-frame angles map directly to canvas (matching the AIM-9
  // reticle and gun bore line); the HUD canvas is body-fixed, so it
  // already rolls with the aircraft.
  _drawGunFunnel(ctx) {
    const gun = this.weaponSystem?.gunNode;
    if (!gun) return;

    const cfg = HUD.gun;
    const fcfg = cfg.funnel;
    const a = this.aircraft;

    gun.getWorldPosition(_gunPos);
    _gunDir.set(0, 1, 0).applyQuaternion(gun.getWorldQuaternion(_tmpQuat));

    // Body-fixed gun offset (from aircraft origin) and direction
    _invQuat.copy(a.quaternion).invert();
    _gunLocal.set(
      _gunPos.x - a.position.x,
      _gunPos.y - a.position.y,
      _gunPos.z - a.position.z,
    ).applyQuaternion(_invQuat);
    _gunDirBody.copy(_gunDir).applyQuaternion(_invQuat);

    const ax = a.position.x, ay = a.position.y, az = a.position.z;
    const vx = a.velocity.x, vy = a.velocity.y, vz = a.velocity.z;

    // World-frame angular velocity → axis/magnitude for past-pose rotation
    const omega = a.angularVelocity;
    const omegaMag = omega.length();
    const hasOmega = omegaMag > 1e-5;
    const oxA = hasOmega ? omega.x / omegaMag : 0;
    const oyA = hasOmega ? omega.y / omegaMag : 0;
    const ozA = hasOmega ? omega.z / omegaMag : 0;

    const wind = this.windManager?.wind;
    const wx = wind ? wind.x : 0;
    const wy = wind ? wind.y : 0;
    const wz = wind ? wind.z : 0;

    // Funnel integrates finer than the pipper — pipper's 0.04s step is sized
    // around its per-step terrain raycast, but the funnel doesn't raycast, and
    // a coarser step accumulates a noticeable drag-bias against WeaponSystem's
    // ~0.016s frame integration over the full TOF.
    const simStep = Math.min(cfg.simStep, 0.02);
    const g = ENV.gravity;
    const mv = GAU8.muzzleVelocity;

    const N = fcfg.numSegments;
    const tMin = fcfg.tofMin, tMax = fcfg.tofMax;
    const halfWS = fcfg.targetWingspan * 0.5;

    const yawOff = cfg.yawOffsetDeg;
    const pitchOff = cfg.pitchOffsetDeg;
    const cx = this.cx, cy = this.cy, ppd = this.pxPerDeg;

    const lXs = this._funnelLX || (this._funnelLX = new Float32Array(N + 1));
    const lYs = this._funnelLY || (this._funnelLY = new Float32Array(N + 1));
    const rXs = this._funnelRX || (this._funnelRX = new Float32Array(N + 1));
    const rYs = this._funnelRY || (this._funnelRY = new Float32Array(N + 1));

    for (let i = 0; i <= N; i++) {
      const tof = tMin + (tMax - tMin) * (i / N);

      // Past quaternion: q_old such that rotating it forward by ω·tof yields
      // current q. Aircraft.js uses world-frame ω with q.premultiply(dq), so
      // the inverse is q_old = dq(-ω·tof) · q.
      if (hasOmega) {
        const halfAng = -omegaMag * tof * 0.5;
        const sH = Math.sin(halfAng);
        _dq.set(oxA * sH, oyA * sH, ozA * sH, Math.cos(halfAng));
        _qOld.copy(a.quaternion).premultiply(_dq);
      } else {
        _qOld.copy(a.quaternion);
      }

      // Past gun world position and direction
      _gunPosOld.copy(_gunLocal).applyQuaternion(_qOld);
      _gunPosOld.x += ax - vx * tof;
      _gunPosOld.y += ay - vy * tof;
      _gunPosOld.z += az - vz * tof;
      _gunDirOld.copy(_gunDirBody).applyQuaternion(_qOld);

      // Initial bullet state (assume aircraft velocity unchanged over TOF)
      let bvx = vx + _gunDirOld.x * mv;
      let bvy = vy + _gunDirOld.y * mv;
      let bvz = vz + _gunDirOld.z * mv;
      let bx = _gunPosOld.x;
      let by = _gunPosOld.y;
      let bz = _gunPosOld.z;

      // Integrate forward by tof (semi-implicit Euler, matches WeaponSystem)
      let remaining = tof;
      while (remaining > 1e-6) {
        const step = remaining > simStep ? simStep : remaining;
        const rvx = bvx - wx, rvy = bvy - wy, rvz = bvz - wz;
        const rspeed = Math.sqrt(rvx * rvx + rvy * rvy + rvz * rvz);
        const drag = BULLET_DRAG_K * rspeed;
        bvx += (-drag * rvx) * step;
        bvy += (-g - drag * rvy) * step;
        bvz += (-drag * rvz) * step;
        bx += bvx * step;
        by += bvy * step;
        bz += bvz * step;
        remaining -= step;
      }

      // Direction from CURRENT aircraft to bullet → body frame angles
      _dir.set(bx - ax, by - ay, bz - az);
      const range = _dir.length();
      _dir.applyQuaternion(_invQuat);
      const fwd = -_dir.z;
      if (fwd <= 0) {
        lXs[i] = lYs[i] = rXs[i] = rYs[i] = NaN;
        continue;
      }

      const angXDeg = Math.atan2(_dir.x, fwd) * RAD2DEG + yawOff;
      const angYDeg = Math.atan2(_dir.y, fwd) * RAD2DEG + pitchOff;
      const halfAngWSDeg = Math.atan2(halfWS, range) * RAD2DEG;

      lXs[i] = cx + (angXDeg - halfAngWSDeg) * ppd;
      lYs[i] = cy - angYDeg * ppd;
      rXs[i] = cx + (angXDeg + halfAngWSDeg) * ppd;
      rYs[i] = cy - angYDeg * ppd;
    }

    ctx.strokeStyle = this.color;
    ctx.lineWidth = fcfg.lineWidth;

    // Left wall
    ctx.beginPath();
    let active = false;
    for (let i = 0; i <= N; i++) {
      if (Number.isNaN(lXs[i])) { active = false; continue; }
      if (active) ctx.lineTo(lXs[i], lYs[i]);
      else { ctx.moveTo(lXs[i], lYs[i]); active = true; }
    }
    ctx.stroke();

    // Right wall
    ctx.beginPath();
    active = false;
    for (let i = 0; i <= N; i++) {
      if (Number.isNaN(rXs[i])) { active = false; continue; }
      if (active) ctx.lineTo(rXs[i], rYs[i]);
      else { ctx.moveTo(rXs[i], rYs[i]); active = true; }
    }
    ctx.stroke();
  }

  // ── AIM-9 Seeker Reticle ───────────────────────────────────────────────

  /**
   * Circle (~1.5× the velocity-vector circle) that orbits the gun bore
   * cross while no target is acquired and smoothly slews to the tracked
   * enemy once a lock is found. Hidden when the rack is empty.
   */
  _drawAIM9Reticle(ctx, dt) {
    const ws = this.weaponSystem;
    if (!ws || ws.aim9MissilesRemaining === 0) {
      this._aim9Pos = null;
      return;
    }

    const gun = ws.gunNode;
    if (!gun) return;

    const cfg = HUD.aim9;
    const a = this.aircraft;

    // ── Boresight cross screen position (body-fixed; matches _drawGunBoreLine) ──
    // Scale is applied around the raw boresight (no scale needed here since
    // the cross is *at* the boresight) and offsets shift the whole reticle.
    _gunDir.set(0, 1, 0).applyQuaternion(gun.getWorldQuaternion(_tmpQuat));
    _invQuat.copy(a.quaternion).invert();
    _gunDir.applyQuaternion(_invQuat);
    const gunFwd = -_gunDir.z;
    if (gunFwd <= 0) return;
    const boreRawXDeg = Math.atan2(_gunDir.x, gunFwd) * RAD2DEG;
    const boreRawYDeg = Math.atan2(_gunDir.y, gunFwd) * RAD2DEG;
    const boreXDeg = boreRawXDeg + cfg.yawOffsetDeg;
    const boreYDeg = boreRawYDeg + cfg.pitchOffsetDeg;
    const boreX = this.cx + boreXDeg * this.pxPerDeg;
    const boreY = this.cy - boreYDeg * this.pxPerDeg;

    // ── Desired position: locked target screen pos, else spin orbit ──────
    let desiredX, desiredY;
    let lockedScreen = false;

    const target = ws.trackedTarget;
    if (target) {
      const tp = target.flight ? target.flight.position
              : target.hitVolume ? target.hitVolume.center
              : null;
      if (tp) {
        _dir.set(tp.x - a.position.x, tp.y - a.position.y, tp.z - a.position.z);
        _invQuat.copy(a.quaternion).invert();
        _dir.applyQuaternion(_invQuat);
        const tFwd = -_dir.z;
        if (tFwd > 0) {
          // Body-frame angles map directly to canvas position (same as the
          // bore cross), so the reticle stays pinned to the target as the
          // aircraft rolls — the body-fixed HUD canvas already carries the
          // roll. Scale stretches the angular distance from the raw boresight
          // and the offset shifts the result; a target sitting on the
          // boresight stays pinned to the cross regardless of scale.
          const angXDeg = (Math.atan2(_dir.x, tFwd) * RAD2DEG - boreRawXDeg) * cfg.yawScale
            + boreRawXDeg + cfg.yawOffsetDeg;
          const angYDeg = (Math.atan2(_dir.y, tFwd) * RAD2DEG - boreRawYDeg) * cfg.pitchScale
            + boreRawYDeg + cfg.pitchOffsetDeg;
          desiredX = this.cx + angXDeg * this.pxPerDeg;
          desiredY = this.cy - angYDeg * this.pxPerDeg;
          lockedScreen = true;
        }
      }
    }

    // Spin advances continuously so the orbit stays phase-stable across
    // lock/unlock cycles (resuming search picks up where it left off).
    this._aim9Spin += dt;

    if (!lockedScreen) {
      // Search — orbit the boresight cross at a steady rate
      const ang = (this._aim9Spin / Math.max(0.001, cfg.spinPeriod)) * Math.PI * 2;
      desiredX = boreX + Math.cos(ang) * cfg.spinRadiusPx;
      desiredY = boreY + Math.sin(ang) * cfg.spinRadiusPx;
    }

    // ── Animate toward desired position ──────────────────────────────────
    // Transitions (acquire / lose / target switch) trigger a brief smooth
    // lerp; the rest of the time we snap, so the orbit traces precisely and
    // the locked reticle tracks the target without lag.
    const newTarget = lockedScreen ? target : null;
    const stateChanged = (newTarget !== this._aim9LastTarget);
    if (stateChanged) this._aim9Transition = cfg.transitionTime;
    this._aim9LastTarget = newTarget;

    if (this._aim9Pos === null) {
      this._aim9Pos = { x: desiredX, y: desiredY };
      this._aim9Transition = 0;
    } else if (this._aim9Transition > 0) {
      this._aim9Transition = Math.max(0, this._aim9Transition - dt);
      const k = 1 - Math.exp(-cfg.lockSmoothing * dt);
      this._aim9Pos.x += (desiredX - this._aim9Pos.x) * k;
      this._aim9Pos.y += (desiredY - this._aim9Pos.y) * k;
    } else {
      this._aim9Pos.x = desiredX;
      this._aim9Pos.y = desiredY;
    }

    ctx.strokeStyle = this.color;
    ctx.lineWidth = cfg.lineWidth;
    ctx.beginPath();
    ctx.arc(this._aim9Pos.x, this._aim9Pos.y, cfg.radiusPx, 0, Math.PI * 2);
    ctx.stroke();
  }

  // ── AGM-65 Maverick Reticle ────────────────────────────────────────────
  //
  // A ground-aim crosshair drawn where the Maverick seeker is pointing on the
  // terrain — visually the gun's air-to-ground pipper, but with exactly four
  // dashes around the centre. Hidden when no Maverick is mounted. The seeker's
  // world aim point is projected into the body-fixed HUD frame (same math as
  // the gun pipper), so it tracks the target as the aircraft rolls. Returns its
  // canvas position (shared scratch object) when drawn.
  _drawMaverickReticle(ctx, roll) {
    const seeker = this.maverickSeeker;
    if (!seeker || !seeker.active) return;

    const cfg = HUD.maverick;
    const a = this.aircraft;
    const p = seeker.losPoint;

    _dir.set(p.x - a.position.x, p.y - a.position.y, p.z - a.position.z);
    _invQuat.copy(a.quaternion).invert();
    _dir.applyQuaternion(_invQuat);

    const fwd = -_dir.z;
    if (fwd <= 0) return; // aim point behind the aircraft — don't draw

    const angXDeg = Math.atan2(_dir.x, fwd) * RAD2DEG + cfg.yawOffsetDeg;
    const angYDeg = Math.atan2(_dir.y, fwd) * RAD2DEG + cfg.pitchOffsetDeg;

    const rawX = angXDeg * this.pxPerDeg;
    const rawY = -angYDeg * this.pxPerDeg;
    const cosR = Math.cos(roll);
    const sinR = Math.sin(roll);
    const x = this.cx + rawX * cosR + rawY * sinR;
    const y = this.cy - rawX * sinR + rawY * cosR;

    const r = cfg.radiusPx;
    const n = 4;                       // exactly four dashes
    const fill = cfg.dashFill;
    const segAng = (Math.PI * 2) / n;
    const dashAng = segAng * fill;
    const startOffset = (segAng - dashAng) * 0.5;

    ctx.strokeStyle = this.color;
    ctx.fillStyle = this.color;
    ctx.lineWidth = cfg.lineWidth;

    ctx.beginPath();
    for (let i = 0; i < n; i++) {
      const a0 = i * segAng + startOffset;
      ctx.moveTo(x + r * Math.cos(a0), y + r * Math.sin(a0));
      ctx.arc(x, y, r, a0, a0 + dashAng);
    }
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(x, y, cfg.dotRadiusPx, 0, Math.PI * 2);
    ctx.fill();

    _pipperPos.x = x; _pipperPos.y = y;
    return _pipperPos;
  }

  // ── Airspeed (left side) ───────────────────────────────────────────────

  _drawAirspeed(ctx, airspeedKts) {
    const { cx, cy, color } = this;
    const x = cx - 200;
    const y = cy - 10;

    ctx.fillStyle = color;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    this._setFont(ctx, this.fontSize * 1.3, true);
    ctx.fillText(Math.round(airspeedKts).toString(), x, y);
  }

  // ── Altitude (right side) ──────────────────────────────────────────────

  _drawAltitude(ctx, altitudeFt) {
    const { cx, cy, color } = this;
    const x = cx + 200;
    const y = cy - 10;

    ctx.fillStyle = color;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    this._setFont(ctx, this.fontSize * 1.3, true);
    ctx.fillText(Math.round(altitudeFt).toString(), x, y);
  }

  // ── G-Meter (upper left) ──────────────────────────────────────────────

  _drawGMeter(ctx, gLoad) {
    const { cx, cy, color } = this;
    const x = cx - 200;
    const y = cy - 200;

    ctx.fillStyle = color;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    this._setFont(ctx, this.fontSize);
    ctx.fillText(gLoad.toFixed(1), x, y);
  }

  // ── Flight Path Angle (right side, below altitude) ─────────────────────

  _drawFPA(ctx, fpaDeg) {
    const { cx, cy, color } = this;
    const x = cx + 215;
    const y = cy + 25;

    ctx.fillStyle = color;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    this._setFont(ctx, this.fontSize * 1.3);
    ctx.fillText(`${Math.round(fpaDeg)}`, x, y);
  }

  // ── Heading Tape (bottom center) ───────────────────────────────────────

  _drawHeadingTape(ctx, headingDeg) {
    const { cx, color } = this;
    const tapeY = this.canvas.height - 280;
    const tapeWidth = 80; // visible half-width
    const pxPerHdgDeg = 6;

    ctx.save();

    // Clip to tape region
    ctx.beginPath();
    ctx.rect(cx - tapeWidth, tapeY - 20, tapeWidth * 2, 40);
    ctx.clip();

    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 1.5;
    this._setFont(ctx, this.fontSize * 0.75);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    // Draw ticks across the visible range
    const startDeg = Math.floor(headingDeg - 40);
    const endDeg = Math.ceil(headingDeg + 40);

    for (let deg = startDeg; deg <= endDeg; deg++) {
      const offset = (headingDeg - deg) * pxPerHdgDeg;
      const normDeg = ((deg % 360) + 360) % 360;

      if (normDeg % 10 === 0) {
        // Major tick + label
        ctx.beginPath();
        ctx.moveTo(cx - offset, tapeY - 4);
        ctx.lineTo(cx - offset, tapeY + 2);
        ctx.stroke();

        const label = (normDeg / 10).toString().padStart(2, '0');
        ctx.fillText(label, cx - offset, tapeY - 20);
      } else if (normDeg % 5 === 0) {
        // Minor tick
        ctx.beginPath();
        ctx.moveTo(cx - offset, tapeY - 4);
        ctx.lineTo(cx - offset, tapeY + 2);
        ctx.stroke();
      }
    }

    ctx.restore();

    // Current heading caret (fixed triangle below tape, pointing up)
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(cx, tapeY + 6);
    ctx.lineTo(cx - 3, tapeY + 10);
    ctx.lineTo(cx + 3, tapeY + 10);
    ctx.closePath();
    ctx.stroke();

    // Heading readout above caret
    // ctx.fillStyle = color;
    // ctx.textAlign = 'center';
    // ctx.textBaseline = 'bottom';
    // this._setFont(ctx, this.fontSize * 0.85);
    // ctx.fillText(Math.round(headingDeg).toString().padStart(3, '0'), cx, tapeY - 22);
  }

  // ── Lower-left labels ─────────────────────────────────────────────────

  _drawLowerLeft(ctx) {
    const { color } = this;
    const x = 310;
    const baseY = this.canvas.height - 310;

    ctx.fillStyle = color;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    this._setFont(ctx, this.fontSize * 0.8);
    ctx.fillText('STPT', x, baseY);

    // Gun-mode labels: ARM between STPT and D5/B1, HEI/ammo above D5/B1
    if (this.weaponSystem?.mode === WeaponMode.GAU8) {
      ctx.fillText('ARM', x, baseY - 40);
    }

    ctx.fillText('D5/B1', x, baseY - 80);

    if (this.weaponSystem?.mode === WeaponMode.GAU8) {
      ctx.fillText(`HEI/${this.weaponSystem.ammo}`, x, baseY - 100);
    }
  }

  // ── Lower-right data block ─────────────────────────────────────────────

  _drawLowerRight(ctx, altitudeFt) {
    const { color } = this;
    const x = this.canvas.width - 320;
    const baseY = this.canvas.height - 400;
    const lineH = this.fontSize + 3;

    ctx.fillStyle = color;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    this._setFont(ctx, this.fontSize * 0.8);

    // Radar altitude
    const radarAlt = Math.max(0, Math.round(altitudeFt));
    ctx.fillText(`${radarAlt}R`, x, baseY);

    // Selected steerpoint
    ctx.fillText('1/MSN001', x, baseY + lineH);

    // Distance to steerpoint
    ctx.fillText('0.5M/DTS', x, baseY + lineH * 2);

    // Time to go / reference time
    const now = new Date();
    const hh = now.getHours().toString().padStart(2, '0');
    const mm = now.getMinutes().toString().padStart(2, '0');
    const ss = now.getSeconds().toString().padStart(2, '0');
    ctx.fillText(`10:9/-${hh}:${mm}:${ss}`, x, baseY + lineH * 3);

    // Current time
    ctx.fillText(`${hh}:${mm}:${ss}`, x, baseY + lineH * 4);
  }
}
